import { Button, Grid, Stack, Typography } from "@mui/material";
import ClientProfileSourceTemplateSection from "./ClientProfileSourceTemplateSection";
import type { ClientProfileViewModel } from "./clientProfilesMapping";

interface Props {
  selectedClient: ClientProfileViewModel;
  onSelectClient: (clientCode: string) => void;
  onSourceTemplateSaved: (clientCode: string, sourceTemplateClientCode: string | undefined) => void;
}

const ClientProfileDetailsSection = ({ selectedClient, onSelectClient, onSourceTemplateSaved }: Props) => {
  return (
    <Stack spacing={2}>
      <Typography variant="h5">Client Profile</Typography>
      <Typography variant="h6">
        {selectedClient.title} ({selectedClient.clientCode})
      </Typography>
      <Grid
        container
        spacing={1.5}
        sx={[
          {
            p: 1.5,
          },
          (theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
          }),
        ]}
      >
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="caption" color="textSecondary">
            Client type
          </Typography>
          <Typography>{selectedClient.type}</Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="caption" color="textSecondary">
            Company type
          </Typography>
          <Typography>{selectedClient.companyType}</Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="caption" color="textSecondary">
            Entity-level access configured
          </Typography>
          <Typography>{selectedClient.entityLevelAccess ? "Yes" : "No"}</Typography>
        </Grid>
      </Grid>
      {selectedClient.companyType === "Demo" && selectedClient.type === "FundManager" && (
        <ClientProfileSourceTemplateSection
          key={selectedClient.clientCode}
          clientCode={selectedClient.clientCode}
          sourceTemplateClientCode={selectedClient.sourceTemplateClientCode}
          onSaved={onSourceTemplateSaved}
        />
      )}
      {selectedClient.type === "FundAdmin" && (
        <Stack spacing={1}>
          <Typography variant="subtitle1">Managed Fund Managers</Typography>
          {selectedClient.managedFundManagers.length === 0 ? (
            <Typography color="textSecondary">No managed Fund Managers.</Typography>
          ) : (
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
              {selectedClient.managedFundManagers.map((managedClient) => (
                <Button
                  key={managedClient.clientCode}
                  variant="outlined"
                  onClick={() => onSelectClient(managedClient.clientCode)}
                >
                  {managedClient.title} ({managedClient.clientCode})
                </Button>
              ))}
            </Stack>
          )}
        </Stack>
      )}
      {selectedClient.type === "FundManager" && (
        <Stack spacing={1}>
          <Typography variant="subtitle1">Parent Fund Admin Organizations</Typography>
          {selectedClient.parentOrganizations.length === 0 ? (
            <Typography color="textSecondary">No parent organization found.</Typography>
          ) : (
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
              {selectedClient.parentOrganizations.map((organization) => (
                <Button
                  key={organization.clientCode}
                  variant="outlined"
                  onClick={() => onSelectClient(organization.clientCode)}
                >
                  {organization.title} ({organization.clientCode})
                </Button>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default ClientProfileDetailsSection;
